import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  Output,
  Signal,
  computed,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  CallabilityEnum,
  CouponBarrierEnum,
  CouponEnum,
  KeyDatesEnum,
  PayoffEnum,
  PayoffSchedulerEnum,
  ProductCaptureGroupsEnum,
  ScheduleEnum,
  ScheduleType,
  SummaryEnum,
} from 'src/app/product-capture/model/enums/product-capture.enums';

import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, EMPTY, Observable, Subject, merge, of } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { LifecycleEventType } from 'src/app/lifecycle-events/models/lifecycle-event-type.enum';
import { ScheduleRowColumnVisibility } from 'src/app/product-capture/model/classes/schedule-row-column-visibility';
import { createScheduleTypeOptionsByType } from 'src/app/product-capture/model/consts/schedule-type.const';
import { YES_NO } from 'src/app/product-capture/model/consts/yes-no.const';
import { COUPON_TYPE } from 'src/app/product-capture/model/enums/coupon-type.enum';
import { ROLL_CONVENTION_ENUM } from 'src/app/product-capture/model/enums/roll-convention.enum';
import { SCHEDULE_TYPE_ENUM } from 'src/app/product-capture/model/enums/schedule-type.enum';
import { DropDownOption } from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { PayoffSchedule } from 'src/app/product-capture/model/interfaces/payoff-schedule.interface';
import { UnderlyingAsset } from 'src/app/product-capture/model/interfaces/underlying-asset.interface';
import { ProductCaptureService } from 'src/app/product-capture/services/product-capture.service';
import { MapPayoffScheduleToDate } from 'src/app/product-capture/utils/map-payoff-schedule-to-date';
import { BarrierType } from 'src/app/static-data/models/consts/barrier-types.const';
import { DcSelectComponent } from 'src/app/ui-kit/dc-select/dc-select.component';
import {
  PAYMENT_LAG_BASE_OPTIONS,
  SCHEDULE_LAG_ITEMS,
  ScheduleStrategyKeys,
} from '../../event-schedules/event-schedules.const';
import { RollDaysModalComponent } from './roll-days-modal/roll-days-modal.component';

type ScheduleConditionalFields = Partial<{
  scheduleType: Signal<boolean>;
  pmtScheduleLag: Signal<boolean>;
  refRateFixingLag: Signal<boolean>;
  accrualScheduleLagBase: Signal<boolean>;
  callNoticeLag: Signal<boolean>;
  callNoticeLagBaseType: Signal<boolean>;
  lastPeriodCallable: Signal<boolean>;
  referenceRate: Signal<boolean>;
  rateReset: Signal<boolean>;
  fixedPeriods: Signal<boolean>;
  floatingPeriods: Signal<boolean>;
}>;

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
})
export class ScheduleComponent implements OnDestroy {
  @Input()
  isRefreshing: boolean;

  @Input()
  productForm: UntypedFormGroup;

  @Input()
  product: any;

  @Input()
  isEditing: boolean;

  // The key to be used in the payload sent to schedule API
  key: ScheduleType;

  // The key to be used in the feature form (i.e. payoff > [coupon | callability | participation]) to access the schedule data
  schedulePayoffKey: PayoffEnum;

  // The key to get either couponType or callEventType
  featureKey: string;

  referenceRateOptions: UnderlyingAsset[] = [];

  scheduleTypeItems: DropDownOption[];

  hideLastRow: boolean = false;

  templateSettings: any = {};

  barrierObservable$ = new Subject();

  accrualScheduleLagBaseTypes: DropDownOption[] = [];

  YES_NO = YES_NO;

  // Replaced in the child components
  allowedScheduleStrategies = [];

  scheduleLagItems = SCHEDULE_LAG_ITEMS;

  pmtLagBaseOptions = PAYMENT_LAG_BASE_OPTIONS;

  readonly ADVANCED_COUPON_PRODUCTS: string[] = [
    COUPON_TYPE.CALL,
    COUPON_TYPE.CONDITIONAL,
    COUPON_TYPE.DIGITAL,
    COUPON_TYPE.FIXED_TO_CALL,
    COUPON_TYPE.FIXED_TO_DIGITAL,
    COUPON_TYPE.FIXED_TO_FORWARD,
    COUPON_TYPE.FORWARD,
    COUPON_TYPE.LOWEST_FX_PERFORMANCE,
    COUPON_TYPE.SNOWBALL,
    COUPON_TYPE.RANGE_ACCRUAL,
    COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL,
  ];

  referenceRateLagLabel = 'Ref. Rate Settlement Lag';

  @Output()
  rowUpdated = new EventEmitter();

  @Output()
  scheduleTypeChanged = new EventEmitter();

  @Input()
  canUserEdit: boolean;

  fieldsVisiblity: ScheduleConditionalFields;

  setupDestroy$ = new Subject();

  destroy$ = new Subject();

  visible = signal(true);

  scheduleVisibility = computed(() =>
    Object.keys(this.fieldsVisiblity).some((key) => this.fieldsVisiblity[key]())
  );

  scheduleRowColumnVisibility: ScheduleRowColumnVisibility =
    new ScheduleRowColumnVisibility();

  constructor(
    protected productCaptureService: ProductCaptureService,
    protected fb: FormBuilder,
    protected injector: Injector
  ) {}

  ngOnInit(): void {
    const featureFormChanged$ = new BehaviorSubject(false);
    this.featureForm
      .get(this.featureKey)
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(100),
        startWith(this.featureForm.get(this.featureKey).value),
        tap(() => featureFormChanged$.next(true))
      )
      .subscribe(() => {
        this.setScheduleTypeItems();
        featureFormChanged$.next(false);
      });

    this.scheduleStrategyControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100),
        distinctUntilChanged(),
        delay(0), // Wait for the scheduleTypeItems to be set
        switchMap((value) => {
          if (
            this.key === ScheduleType.COUPON &&
            this.scheduleTypeValue === SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY
          )
            return EMPTY;

          return of(value);
        })
      )
      .subscribe(() => this.applyDefaultScheduleType());

    this.setup();
  }

  // Override in child components
  setFieldsVisibility(): void {}

  showField(
    fieldName: string | AbstractControl,
    callback: (value: any) => boolean
  ): Signal<boolean> {
    const fieldControl =
      typeof fieldName === 'string'
        ? this.scheduleForm.get(fieldName)
        : fieldName;

    const valueChanges$ = fieldControl.valueChanges.pipe(
      startWith(fieldControl.value),
      map(callback),
      takeUntil(this.destroy$)
    );

    return toSignal(valueChanges$, { injector: this.injector });
  }

  // Form building / row adding
  newSchedule(rowData: PayoffSchedule): UntypedFormGroup {
    const group = this.fb.group<any>({
      period: [rowData.period],
      unadjustedPaymentDate: [rowData.unadjustedPaymentDate],
      adjustedPaymentDate: [rowData.adjustedPaymentDate],
      type: [rowData.type],
      accrualPeriodStart: [rowData.accrualPeriodStart],
      accrualPeriodEnd: [rowData.accrualPeriodEnd],
      accrualPeriodInclusion: [rowData.accrualPeriodInclusion],
      dayCountFraction: [rowData.dayCountFraction],
      dayCountFractionMeta: [rowData.dayCountFractionMeta],
      rateDeterminationDate: [rowData.rateDeterminationDate],
      barrierLevel: [rowData.barrierLevel],
      lowerLevel: [rowData.lowerBarrierLevel],
      upperLevel: [rowData.upperBarrierLevel],
      observations: this.fb.array(rowData.observations),
    });

    if (this.getCallEventType() === LifecycleEventType.ISSUER_CALL) {
      group.addControl(
        'callNoticeDate',
        new FormControl(rowData?.callNoticeDate)
      );
    }

    if (
      this.isAutocallOrIssuerCall() &&
      this.isSteppedAmountType() &&
      this.key === ScheduleType.CALLABILITY
    ) {
      group.addControl(
        'redemptionAmountPayable_relative',
        new FormControl(rowData?.redemptionAmountPayable_relative)
      );
    }

    return group;
  }

  addRow(rowData: PayoffSchedule, scheduleType?: PayoffSchedulerEnum): void {
    this.getSchedule<FormArray>(scheduleType).push(this.newSchedule(rowData), {
      emitEvent: false,
    });
  }

  findUnderlyingAssetReferenceRate(
    referenceRateDisplayValue: string
  ): UnderlyingAsset {
    return this.referenceRateOptions.find(
      (asset) =>
        asset.assetId + ' | ' + asset.longName === referenceRateDisplayValue
    );
  }

  getComputedPaymentLagVisibility(): Signal<boolean> {
    const isScheduleVisible = this.getScheduleTypeVisibility();
    const isValidScheduleType = this.getPaymentScheduleLagVisibility();
    return computed(() => isScheduleVisible() && isValidScheduleType());
  }

  getPaymentScheduleLagVisibility(): Signal<boolean> {
    const validScheduleTypes = [
      SCHEDULE_TYPE_ENUM.OBS_PRIMARY,
      SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY,
      SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY,
    ];

    return this.showField(
      this.scheduleType,
      (scheduleType: SCHEDULE_TYPE_ENUM) =>
        validScheduleTypes.includes(scheduleType)
    );
  }

  openRollDaysModal(modal: RollDaysModalComponent): void {
    modal.openModal().subscribe(() => modal.convertPeriods(false));
  }

  updateField(select: DcSelectComponent): void {
    if (select.control.value === undefined) {
      // Convert undefined to null to prevent the pruned data function from removing the field
      select.control.setValue(null);
    }
  }

  getScheduleTypeVisibility(): Signal<boolean> {
    return this.showField(
      this.scheduleStrategyControl,
      (scheduleStrategy: ScheduleStrategyKeys) =>
        this.allowedScheduleStrategies.includes(scheduleStrategy)
    );
  }

  updateProduct(): void {
    this.rowUpdated.emit();
  }

  getCouponValue(keyName: CouponEnum): string {
    return this.coupon?.get(keyName)?.value || '';
  }

  getCouponType(): COUPON_TYPE {
    return this.getCouponValue(CouponEnum.couponType) as COUPON_TYPE;
  }

  get barrierTypeControl(): AbstractControl {
    return this.featureForm.get(CouponEnum.barrier)?.get('type');
  }

  getCallEventType(): string {
    return this.callability?.get(CallabilityEnum.callEventType)?.value || '';
  }

  get scheduleStrategyControl(): UntypedFormControl {
    return this.scheduleSettingsForm.get(
      ScheduleEnum.scheduleStrategy
    ) as UntypedFormControl;
  }

  get scheduleStrategy(): string {
    return this.scheduleStrategyControl?.value || '';
  }

  getBarrierValue(keyName: CouponBarrierEnum): string {
    return this.barrierControl?.get(keyName)?.value || '';
  }

  get barrierControl(): AbstractControl {
    return this.featureForm?.get('barrier');
  }

  getRollConvention(keyName: KeyDatesEnum): string {
    if (keyName === KeyDatesEnum.tradingRollConvention) {
      return this.summary.get(keyName)?.value || ROLL_CONVENTION_ENUM.FOLLOWING;
    }

    return this.productCaptureService.getSettlementRollConvention(
      this.productType,
      this.summary.get(keyName)?.value
    );
  }

  // API call to getSchedules -- Override in child components
  getSchedulePayload(): any {}

  // Reading in data to the modal
  setup(): void {
    this.setupDestroy$.next(true);

    if (!this.fieldsVisiblity) {
      this.setFieldsVisibility();
    }

    merge(
      // Barrier Level auto update
      this.createObservableForBarrierField(ScheduleEnum.barrier),
      this.createObservableForBarrierField(ScheduleEnum.barrierType),
      this.createObservableForBarrierField(ScheduleEnum.barrierStepSize),
      this.createObservableForBarrierField(ScheduleEnum.deferredPeriods).pipe(
        debounceTime(100)
      ),
      // Redemption Amount auto update
      this.createObservableForBarrierField(CallabilityEnum.amount),
      this.createObservableForBarrierField(CallabilityEnum.amountType),
      this.createObservableForBarrierField(CallabilityEnum.amountStepSize),
      // Range Accrual auto update
      this.createObservableForBarrierField(ScheduleEnum.lowerLevel),
      this.createObservableForBarrierField(ScheduleEnum.lowerBarrierStepSize),
      this.createObservableForBarrierField(ScheduleEnum.upperLevel),
      this.createObservableForBarrierField(ScheduleEnum.upperBarrierStepSize),
      this.createObservableForBarrierField(ScheduleEnum.fixedPeriods)
    )
      .pipe(takeUntil(this.destroy$), takeUntil(this.setupDestroy$))
      .subscribe(() => {
        this.scheduleRowColumnVisibility =
          ScheduleRowColumnVisibility.fromValues({
            ...this.getColumnsByScheduleType(),
          });
        this.barrierObservable$.next(this.barrierTypeControl?.value);
      });

    this.updateScheduleData();
    this.changeObsDatesVisibility(this.scheduleData);

    if (this.key === 'participation') {
      return;
    }
  }

  setScheduleTypeItems(): void {
    this.scheduleTypeItems = createScheduleTypeOptionsByType(
      this.key,
      this.summary.get(SummaryEnum.underlyingAssetsGroup).enabled
    );

    if (
      !this.scheduleTypeItems.find(
        (item: DropDownOption) => item.id === this.scheduleTypeValue
      )
    ) {
      this.scheduleType.setValue(null);
      this.scheduleType.markAsUntouched();
    }

    this.applyDefaultScheduleType();
  }

  // Override in child components
  applyDefaultScheduleType(): void {}

  createObservableForBarrierField(fieldName: string): Observable<any> {
    const control = this.featureForm.get(fieldName);
    return control
      ? control.valueChanges.pipe(
          distinctUntilChanged(),
          startWith(control.value),
          takeUntil(this.setupDestroy$)
        )
      : of(null);
  }

  updateTemplateSettings(): void {
    this.templateSettings = {
      obsDateColWidth:
        this.scheduleTypeValue === SCHEDULE_TYPE_ENUM.PAYMENT_ONLY
          ? '127.5px'
          : '170px',
    };

    this.scheduleRowColumnVisibility = ScheduleRowColumnVisibility.fromValues({
      ...this.getColumnsByScheduleType(),
    });
  }

  getScheduleFormValue(): any {
    // Leaving this in an array in case we need to add more fields in the future
    const skippedFields = ['accrualScheduleLagBase'];

    const rawValue = this.scheduleForm.getRawValue();

    if (!this.fieldsVisiblity) {
      return rawValue;
    }

    let cleanedValue = Object.keys(this.fieldsVisiblity).reduce(
      (acc: any, key: string) => {
        if (skippedFields.includes(key)) {
          return acc;
        }

        const visible: boolean = this.fieldsVisiblity[key]();
        if (!visible && key in acc) {
          acc[key] = null;
        }
        return acc;
      },
      rawValue
    );

    // Clear roll configs
    Object.keys(cleanedValue)
      .filter((key) => key.indexOf('RollConfig') > -1)
      .forEach((rollConfig) => {
        const hasRollConfig = Object.keys(cleanedValue[rollConfig]).some(
          (key) => !!cleanedValue[rollConfig][key]
        );
        if (!hasRollConfig) {
          const { [rollConfig]: _, ...rest } = cleanedValue;
          cleanedValue = rest;
        } else {
          Object.keys(cleanedValue[rollConfig]).forEach((key) => {
            if (!cleanedValue[rollConfig][key]) {
              delete cleanedValue[rollConfig][key];
            }
          });

          if (cleanedValue[rollConfig].rollDay) {
            cleanedValue[rollConfig].rollDay = parseInt(
              cleanedValue[rollConfig].rollDay
            );
          }
        }
      });

    return cleanedValue;
  }

  updateScheduleData(schedules?: any): void {
    this.hideLastRow =
      this.key === ScheduleType.CALLABILITY &&
      !this.featureForm.get(ScheduleEnum.lastPeriodCallable)?.value;

    const keyName = this.getScheduleName();
    let mapData = true;

    if (!schedules) {
      schedules = this.product?.summary.schedule;

      // Parse the schedules saved in the product
      if (schedules) {
        const schedule = schedules[this.key]?.schedule;
        mapData = false;

        if (this.key !== ScheduleType.PARTICIPATION) {
          schedules = { [keyName]: schedule ?? [] };
        } else {
          schedules = Object.keys(schedules[this.key]).reduce(
            (acc: any, key: string) => {
              acc['participationSchedule'][key] =
                schedules[this.key]?.[key]?.schedule ?? [];
              return acc;
            },
            { participationSchedule: {} }
          );
        }
      }
    }

    if (!schedules) {
      return;
    }

    if (keyName === PayoffSchedulerEnum.participationSchedule) {
      let upsideScheduleData =
        schedules[PayoffSchedulerEnum.participationSchedule][
          PayoffSchedulerEnum.upsideSchedule
        ];
      let downsideScheduleData =
        schedules[PayoffSchedulerEnum.participationSchedule][
          PayoffSchedulerEnum.downsideSchedule
        ];
      let baseScheduleData =
        schedules[PayoffSchedulerEnum.participationSchedule][
          PayoffSchedulerEnum.baseSchedule
        ];

      if (mapData) {
        upsideScheduleData = MapPayoffScheduleToDate(upsideScheduleData);
        downsideScheduleData = MapPayoffScheduleToDate(downsideScheduleData);
        baseScheduleData = MapPayoffScheduleToDate(baseScheduleData);
      }

      [
        PayoffSchedulerEnum.downsideSchedule,
        PayoffSchedulerEnum.upsideSchedule,
        PayoffSchedulerEnum.baseSchedule,
      ].forEach((scheduleType) => {
        this.getSchedule<FormArray>(scheduleType)?.clear();
        const dataToLoad =
          scheduleType === PayoffSchedulerEnum.downsideSchedule
            ? downsideScheduleData
            : scheduleType === PayoffSchedulerEnum.upsideSchedule
            ? upsideScheduleData
            : baseScheduleData;
        dataToLoad.forEach((scheduleObject: any) => {
          const keyForType =
            scheduleType === PayoffSchedulerEnum.downsideSchedule
              ? 'downsideType'
              : scheduleType === PayoffSchedulerEnum.upsideSchedule
              ? 'upsideType'
              : 'baseType';
          const valueForType = scheduleObject.type;

          const scheduleData = mapData
            ? MapPayoffScheduleToDate(scheduleObject.periods)
            : scheduleObject.periods;
          scheduleData.forEach((element: PayoffSchedule) => {
            element[keyForType] = valueForType;
            element.type = valueForType;
            this.addRow(element, scheduleType);
          });
        });
      });
    } else {
      const scheduleData = mapData
        ? MapPayoffScheduleToDate(schedules[keyName])
        : schedules[keyName];

      // If custom barrier levels have been defined, add them to scheduleData
      // So that they don't get lost when the schedule is cleared
      if (
        this.featureForm.get('barrier')?.get('type')?.value ===
        BarrierType.CUSTOM
      ) {
        if (scheduleData.length) {
          scheduleData.forEach((row, index) => {
            const barrierLevel = this.getSchedule<FormArray>()
              .at(index)
              ?.get('barrierLevel');
            if (barrierLevel) {
              row.barrierLevel = barrierLevel.value;
            }

            const lowerLevel = this.getSchedule<FormArray>()
              .at(index)
              ?.get('lowerLevel');
            if (lowerLevel) {
              row.lowerLevel = lowerLevel.value;
            }

            const upperLevel = this.getSchedule<FormArray>()
              .at(index)
              ?.get('upperLevel');
            if (upperLevel) {
              row.upperLevel = upperLevel.value;
            }
          });
        }
      }

      // Extract redemption values from the existing schedule
      const redemptionValues = this.getSchedule<FormArray>().controls.map(
        (control) => ({
          redemptionAmountPayable_relative: control.get(
            'redemptionAmountPayable_relative'
          )?.value,
        })
      );

      // Merge redemption values back into scheduleData
      scheduleData.forEach((row, index) => {
        if (redemptionValues[index] && row) {
          row.redemptionAmountPayable_relative =
            redemptionValues[index].redemptionAmountPayable_relative;
        }
      });

      // Clear the existing schedule array
      this.getSchedule<FormArray>()?.clear();

      // Add the updated rows back to the schedule array
      scheduleData?.forEach((row) => this.addRow(row));
    }

    this.updateTemplateSettings();
    this.changeObsDatesVisibility(schedules[keyName]);
  }

  changeObsDatesVisibility(schedules: any[]): void {
    if (!schedules?.length) {
      this.scheduleRowColumnVisibility.adjObsDate = false;
      this.scheduleRowColumnVisibility.unadjObsDate = false;

      return;
    }

    let hasNoObsDates = schedules.every(({ observations }) => {
      if (!observations?.length) {
        return true;
      }

      return observations.every(
        ({
          adjustedObsEndDate,
          adjustedObsStartDate,
          unadjustedObsEndDate,
          unadjustedObsStartDate,
        }) => {
          return (
            !adjustedObsEndDate &&
            !adjustedObsStartDate &&
            !unadjustedObsEndDate &&
            !unadjustedObsStartDate
          );
        }
      );
    });

    if (hasNoObsDates) {
      this.scheduleRowColumnVisibility.adjObsDate = false;
      this.scheduleRowColumnVisibility.unadjObsDate = false;
    }
  }

  getColumnsByScheduleType(): {} {
    return {};
  }

  private getScheduleName(): string {
    if (this.key === ScheduleType.COUPON) {
      return PayoffSchedulerEnum.couponSchedule;
    } else if (this.key === ScheduleType.PARTICIPATION) {
      return PayoffSchedulerEnum.participationSchedule;
    } else {
      return PayoffSchedulerEnum.earlyRedemptionSchedule;
    }
  }

  isFloatingCoupon(): boolean {
    const type = this.getCouponType();
    return (
      type === COUPON_TYPE.FLOATING ||
      type === COUPON_TYPE.FIXED_TO_FLOATING ||
      type === COUPON_TYPE.FLOATING_TO_FIXED
    );
  }

  get productType(): string {
    return (
      this.productForm
        .get(ProductCaptureGroupsEnum.summary)
        ?.get(SummaryEnum.productType)?.value ?? ''
    );
  }

  get scheduleTypeValue(): SCHEDULE_TYPE_ENUM {
    return this.scheduleType?.value;
  }

  get scheduleType(): UntypedFormControl {
    return this.scheduleForm.get(
      ScheduleEnum.scheduleType
    ) as UntypedFormControl;
  }

  // Getters for modal form
  getSchedule<T extends FormGroup | FormArray>(type?: PayoffSchedulerEnum): T {
    if (this.key === ScheduleType.PARTICIPATION) {
      return this.scheduleForm.get(type)?.get('schedule') as T;
    }
    return this.scheduleForm.get(CouponEnum.schedule) as T;
  }

  get participation(): UntypedFormControl {
    return this.productForm
      .get(ProductCaptureGroupsEnum.payoff)
      .get(PayoffEnum.participation) as UntypedFormControl;
  }

  get coupon(): UntypedFormControl {
    return this.productForm
      .get(ProductCaptureGroupsEnum.payoff)
      ?.get(PayoffEnum.coupon) as UntypedFormControl;
  }

  get scheduleData(): PayoffSchedule[] {
    return this.scheduleForm.get(CouponEnum.schedule)?.value;
  }

  get accrualScheduleLagBase(): UntypedFormControl {
    return this.scheduleForm.get(
      ScheduleEnum.accrualScheduleLagBase
    ) as UntypedFormControl;
  }

  get callability(): UntypedFormControl {
    return this.productForm
      .get(ProductCaptureGroupsEnum.payoff)
      ?.get(PayoffEnum.callability) as UntypedFormControl;
  }

  get refRateFixingLag(): UntypedFormControl {
    return this.scheduleForm.get(
      ScheduleEnum.refRateFixingLag
    ) as UntypedFormControl;
  }

  get summary(): UntypedFormGroup {
    return this.productForm.get(
      ProductCaptureGroupsEnum.summary
    ) as UntypedFormGroup;
  }

  get payoffForm(): UntypedFormGroup {
    return this.productForm.get(
      ProductCaptureGroupsEnum.payoff
    ) as UntypedFormGroup;
  }

  get keyDatesForm(): FormGroup {
    return this.summary.get(SummaryEnum.keyDates) as FormGroup;
  }

  // Represents the current schedule form
  get scheduleForm(): UntypedFormGroup {
    return this.summary
      .get(SummaryEnum.schedule)
      .get(this.key) as UntypedFormGroup;
  }

  get callScheduleForm(): UntypedFormGroup {
    return this.summary
      .get(SummaryEnum.schedule)
      .get(ScheduleType.CALLABILITY) as UntypedFormGroup;
  }

  get couponScheduleForm(): UntypedFormGroup {
    return this.summary
      .get(SummaryEnum.schedule)
      .get(ScheduleType.COUPON) as UntypedFormGroup;
  }

  get scheduleSettingsForm(): UntypedFormGroup {
    return this.summary.get(SummaryEnum.schedule) as UntypedFormGroup;
  }

  get featureForm(): UntypedFormGroup {
    return this.payoffForm.get(this.schedulePayoffKey) as UntypedFormGroup;
  }

  getScheduleFormFieldValue(fieldName: string): any {
    return this.scheduleForm.get(fieldName)?.value;
  }

  get frequency(): string {
    return this.key === ScheduleType.COUPON
      ? this.couponFrequency
      : this.key === ScheduleType.CALLABILITY
      ? this.callabilityFrequency
      : '';
  }

  get couponFrequency(): string {
    return this.getCouponType() === 'NONE'
      ? ''
      : this.getCouponValue(CouponEnum.frequency);
  }

  get callabilityFrequency(): string {
    return this.getCallEventType() === 'NONE'
      ? ''
      : this.callability?.get(CallabilityEnum.frequency)?.value || '';
  }

  get tenor(): string {
    return this.scheduleForm.get('tenor').value;
  }

  get isBlocked(): boolean {
    const check =
      this.scheduleForm.get(SummaryEnum.tenor).value === null ||
      this.scheduleForm.get(SummaryEnum.frequency).value === null;
    return check;
  }

  hasEditedDates(): boolean {
    return this.getSchedule<FormArray>()?.value?.some((row) =>
      this.isScheduleRowEdited(row)
    );
  }

  private isScheduleRowEdited(row: PayoffSchedule): boolean {
    return Object.keys(row).some(
      (key) =>
        !!row[key]?.edited ||
        row?.observations?.some((obs) =>
          Object.keys(obs).some((obsKey) => !!obs[obsKey]?.edited)
        )
    );
  }

  private isAutocallOrIssuerCall(): boolean {
    const callEventType = this.callability.get(
      CallabilityEnum.callEventType
    ).value;
    return [
      LifecycleEventType.AUTOCALL,
      LifecycleEventType.ISSUER_CALL,
    ].includes(callEventType);
  }

  private isSteppedAmountType(): boolean {
    return (
      this.callability.get(CallabilityEnum.amountType).value ===
      BarrierType.STEPPED
    );
  }

  ngOnDestroy(): void {
    this.scheduleForm.setControl('schedule', new FormArray([]));
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
